import React, { useEffect, useState } from "react";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import authStore from "./stores/AuthStore";
import "./App.scss";
import Header from "./components/Header";
import RootRouter from "./pages/RootRouter";
import Feedback from "./components/Feedback";
import configStore from "./stores/ConfigStore";
import MobileNav from "./components/MobileNav";
import Footer from "./components/Footer";
import usePushNotification from "./hooks/usePushNotification";
import useTranslation from "./hooks/useTranslation";
import { WifiOff } from "@mui/icons-material";
import caseStore from "./stores/CaseStore";
import { useNavigate } from "react-router-dom";

const App = observer(() => {
  const { t } = useTranslation();
  const { initializePushNotifications } = usePushNotification();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const navigate = useNavigate();

  useEffect(() => {
    authStore.init().then((isAuthenticated) => {
      if (isAuthenticated) {
        configStore.initAppConfig().then(async () => {
          await configStore.fetchDictionaryContent();

          // Initialize push notifications for mobile devices
          if (window.cordova && !!PushNotification) {
            initializePushNotifications();
          }

          if (caseStore.presetCaseItem) {
            navigate(`/case/new`);
          } else if (localStorage.getItem("presetCaseItem")) {
            const presetData = JSON.parse(
              localStorage.getItem("presetCaseItem") || "{}",
            );
            caseStore.presetCaseItem = presetData;
            localStorage.removeItem("presetCaseItem");
            navigate(`/case/new`);
          }

          setIsLoaded(true);
        });
      } else {
        configStore.fetchFeatureFlags().then(() => setIsLoaded(true));
      }
    });

    return () => setIsLoaded(false);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    const handleMessage = (event: any) => {
      if (event.data?.action === "preset-case") {
        if (authStore.isAuthenticated) {
          caseStore.presetCaseItem = event.data;
          navigate(`/case/new`);
        } else {
          localStorage.setItem("presetCaseItem", JSON.stringify(event.data));
        }
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return isOnline ? (
    <div className="appContainer">
      {authStore.isAuthenticated && <Header />}
      {isLoaded ? (
        <>
          <main>
            <RootRouter />
          </main>
          <footer>
            <Footer />
          </footer>
        </>
      ) : (
        <div className="loadingContainer mt pt">
          <CircularProgress />
        </div>
      )}
      {authStore.isAuthenticated && (
        <div className="hidden-lg">
          <MobileNav />
        </div>
      )}
      <Feedback />
    </div>
  ) : (
    <Stack alignItems="center" justifyContent="center" spacing={2} padding={4}>
      <Typography fontSize={18}>{t("app-require-online")}</Typography>
      <WifiOff style={{ fontSize: 64 }} />
    </Stack>
  );
});

export default App;
